import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ReviewsSection = ({ profile, reviews = [] }) => {
  // Extract review data from profile
  const overallRating = parseFloat(profile?.rating) || 0;
  const totalReviews = profile?.reviewCount || 0;
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showAllReviews, setShowAllReviews] = useState(false);

  const ratingFilters = [
    { label: 'All Reviews', value: 'all' },
    { label: '5 Stars', value: 5 },
    { label: '4 Stars', value: 4 },
    { label: '3 Stars', value: 3 },
    { label: '2 Stars', value: 2 },
    { label: '1 Star', value: 1 }
  ];

  // Ensure reviews is an array
  const reviewsArray = Array.isArray(reviews) ? reviews : [];
  
  const filteredReviews = selectedFilter === 'all' 
    ? reviewsArray 
    : reviewsArray.filter(review => review.rating === selectedFilter);

  const displayedReviews = showAllReviews ? filteredReviews : filteredReviews.slice(0, 6);

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    if (Array.isArray(reviews)) {
      reviews.forEach(review => {
        if (review && review.rating && distribution[review.rating] !== undefined) {
          distribution[review.rating]++;
        }
      });
    }
    return distribution;
  };

  const ratingDistribution = getRatingDistribution();

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 30) return `${diffDays} days ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return 'Recently';
    }
  };

  const renderStars = (rating, size = 16) => {
    return [...Array(5)].map((_, index) => (
      <Icon
        key={index}
        name="Star"
        size={size}
        className={`${
          index < rating
            ? 'text-warning fill-current' :'text-secondary-300'
        }`}
      />
    ));
  };

  return (
    <section className="bg-secondary-50 py-8 lg:py-12">
      <div className="content-container">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Icon name="MessageSquare" size={24} className="text-primary" />
            <h2 className="text-2xl font-bold text-text-primary">Client Reviews</h2>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Rating Overview */}
            <div className="lg:col-span-1">
              <div className="card p-6 sticky top-8">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-text-primary mb-2">
                    {overallRating ? overallRating.toFixed(1) : '0.0'}
                  </div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {renderStars(Math.floor(overallRating || 0), 20)}
                  </div>
                  <p className="text-sm text-text-secondary">
                    Based on {totalReviews || 0} {totalReviews === 1 ? 'review' : 'reviews'}
                  </p>
                </div>

                {/* Rating Distribution */}
                <div className="space-y-3 mb-6">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = ratingDistribution[rating];
                    const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    
                    return (
                      <div key={rating} className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary w-6">{rating}</span>
                        <Icon name="Star" size={14} className="text-warning fill-current" />
                        <div className="flex-1 bg-secondary-200 rounded-full h-2">
                          <div
                            className="bg-warning h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-text-muted w-8">{count}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Filter Buttons */}
                <div className="space-y-2">
                  {ratingFilters.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setSelectedFilter(filter.value)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        selectedFilter === filter.value
                          ? 'bg-primary text-white' :'text-text-secondary hover:bg-secondary-100'
                      }`}
                    >
                      {filter.label}
                      {filter.value !== 'all' && (
                        <span className="ml-2 text-xs opacity-75">
                          ({ratingDistribution[filter.value]})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Reviews List */}
            <div className="lg:col-span-3">
              <div className="space-y-6">
                {displayedReviews.map((review, index) => (
                  <div key={index} className="card p-6">
                    {/* Review Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-border flex-shrink-0">
                          {review.clientAvatar ? (
                            <Image
                              src={review.clientAvatar}
                              alt={review.clientName}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full bg-secondary-200 flex items-center justify-center">
                              <Icon name="User" size={20} className="text-text-muted" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-text-primary">{review.clientName}</h3>
                          <p className="text-sm text-text-secondary">{review.clientTitle}</p>
                          <p className="text-xs text-text-muted">{review.projectType}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-1 mb-1">
                          {renderStars(review.rating)}
                        </div>
                        <p className="text-xs text-text-muted">{getTimeAgo(review.date)}</p>
                      </div>
                    </div>

                    {/* Review Content */}
                    <div className="mb-4">
                      <p className="text-text-secondary leading-relaxed">
                        "{review.content}"
                      </p>
                    </div>

                    {/* Project Details */}
                    {review.projectDetails && (
                      <div className="bg-secondary-50 rounded-lg p-4 mb-4">
                        <h4 className="font-medium text-text-primary mb-2">Project: {review.projectDetails.title}</h4>
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Icon name="Calendar" size={14} className="text-text-muted" />
                            <span className="text-text-secondary">Duration: {review.projectDetails.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="DollarSign" size={14} className="text-text-muted" />
                            <span className="text-text-secondary">Budget: {review.projectDetails.budget}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Review Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border">
                      <div className="text-center">
                        <div className="text-lg font-bold text-text-primary">{review.metrics.quality}/5</div>
                        <div className="text-xs text-text-muted">Quality</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-text-primary">{review.metrics.communication}/5</div>
                        <div className="text-xs text-text-muted">Communication</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-text-primary">{review.metrics.timeliness}/5</div>
                        <div className="text-xs text-text-muted">Timeliness</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-text-primary">{review.metrics.value}/5</div>
                        <div className="text-xs text-text-muted">Value</div>
                      </div>
                    </div>

                    {/* Helpful Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors duration-200">
                          <Icon name="ThumbsUp" size={14} />
                          <span>Helpful ({review.helpfulCount})</span>
                        </button>
                        {review.isVerified && (
                          <div className="flex items-center gap-1 text-sm text-success">
                            <Icon name="CheckCircle" size={14} />
                            <span>Verified Client</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-text-muted">
                        {formatDate(review.date)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {filteredReviews.length > 6 && (
                <div className="text-center mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    iconLeft={<Icon name={showAllReviews ? "ChevronUp" : "ChevronDown"} size={16} />}
                  >
                    {showAllReviews 
                      ? 'Show Less Reviews' 
                      : `Show ${filteredReviews.length - 6} More Reviews`
                    }
                  </Button>
                </div>
              )}

              {filteredReviews.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="MessageSquare" size={48} className="text-text-muted mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">No reviews found</h3>
                  <p className="text-text-secondary">Try selecting a different rating filter</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
